import type { SearchSchemaInput } from '@tanstack/react-router';

import type { ProjectStatus } from '../../lib/dto';

/**
 * فلاتر القائمة تُخزَّن في search params (§1.2): الروابط قابلة للمشاركة والرجوع
 * بالتاريخ يحفظ السياق. هذا الملف هو المترجم الوحيد بين الـ URL وفلاتر الاستعلام.
 */

export type StatusFilterId = 'all' | 'active' | 'idea' | 'waiting' | 'done' | 'archived';

export const STATUS_FILTERS: readonly { readonly id: StatusFilterId; readonly label: string }[] = [
  { id: 'all', label: 'الكل' },
  { id: 'active', label: 'نشط' },
  { id: 'idea', label: 'فكرة' },
  { id: 'waiting', label: 'بانتظار' },
  { id: 'done', label: 'منجز' },
  { id: 'archived', label: 'مؤرشف' },
];

const STATUS_IDS: readonly StatusFilterId[] = STATUS_FILTERS.map((filter) => filter.id);

export interface ProjectListSearch {
  readonly status: StatusFilterId;
  readonly stalled: boolean;
}

export const DEFAULT_LIST_SEARCH: ProjectListSearch = { status: 'all', stalled: false };

/**
 * `all` تعني كل ما هو حيّ: المؤرشف يبقى للأبد ويظهر **تحت فلتره** وحده (قرار D10)،
 * فلا يزحم قائمة العمل اليومي بينما لا يُحذف ولا يُخفى.
 */
const LIVE_STATUSES: readonly ProjectStatus[] = ['idea', 'active', 'waiting', 'done'];

export function statusesFor(id: StatusFilterId): readonly ProjectStatus[] {
  switch (id) {
    case 'all':
      return LIVE_STATUSES;
    case 'active':
      return ['active'];
    case 'idea':
      return ['idea'];
    case 'waiting':
      return ['waiting'];
    case 'done':
      return ['done'];
    case 'archived':
      return ['archived'];
  }
}

function asStatusFilterId(value: unknown): StatusFilterId {
  return STATUS_IDS.find((id) => id === value) ?? 'all';
}

/**
 * دخل مختلف عن الخرج بعلامة `SearchSchemaInput` من الموجّه: الخرج كامل الحقول (يقرأه
 * المكوّن بلا فحوص)، أما الدخل فكل حقوله اختيارية — فيبقى `search` **اختياريًا** على
 * كل `<Link to="/projects">` في التطبيق (ومنه الشريط الجانبي) ولا يُجبر أحد على تمرير
 * فلاتر ليصل إلى الشاشة.
 */
export type ProjectListSearchInput = {
  readonly status?: unknown;
  readonly stalled?: unknown;
} & SearchSchemaInput;

/** يتسامح مع أي قيمة في الـ URL: فلتر مجهول يعود إلى الافتراضي بلا شاشة خطأ. */
export function validateProjectListSearch(input: ProjectListSearchInput): ProjectListSearch {
  return {
    status: asStatusFilterId(input.status),
    stalled: input.stalled === true || input.stalled === 'true',
  };
}

export function isDefaultSearch(search: ProjectListSearch): boolean {
  return search.status === DEFAULT_LIST_SEARCH.status && search.stalled === DEFAULT_LIST_SEARCH.stalled;
}
